import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { serviceChargeRepository } from '../repositories/service-charge.repository';
// =============================================================================
// HELPERS
// =============================================================================
function getResponseStatus(err) {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = err.response;
        return response?.status ?? null;
    }
    return null;
}
// Module-level counter for synthetic negative IDs (pending custom charges)
let nextTempId = 0;
// =============================================================================
// STORE
// =============================================================================
export const useServiceChargeStore = defineStore('serviceCharge', () => {
    // ── STATE ──────────────────────────────────────────────────────────────────
    const serviceId = ref(null);
    const attachedTemplateIds = ref([]);
    const customCharges = ref([]);
    const pendingCustomCharges = ref([]);
    const loadingTemplateIds = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    let scopeVersion = 0;
    // ── GETTERS ────────────────────────────────────────────────────────────────
    const isTemplateAttached = computed(() => (templateId) => attachedTemplateIds.value.includes(templateId));
    const isTemplateLoading = computed(() => (templateId) => loadingTemplateIds.value.includes(templateId));
    // ── ACTIONS ────────────────────────────────────────────────────────────────
    async function initForService(id) {
        const version = ++scopeVersion;
        serviceId.value = id;
        attachedTemplateIds.value = [];
        customCharges.value = [];
        pendingCustomCharges.value = [];
        loadingTemplateIds.value = [];
        nextTempId = 0;
        error.value = null;
        isLoading.value = true;
        try {
            const data = await serviceChargeRepository.getForService(id);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            attachedTemplateIds.value = data.templates.map((t) => t.charge_template_id);
            customCharges.value = data.custom;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = 'Error al cargar los cargos del servicio';
        }
        finally {
            if (version === scopeVersion) {
                isLoading.value = false;
            }
        }
    }
    async function attachTemplate(templateId) {
        const version = scopeVersion;
        if (attachedTemplateIds.value.includes(templateId))
            return;
        if (!serviceId.value) {
            // Pending mode: optimistic UI update, no API call
            attachedTemplateIds.value = [...attachedTemplateIds.value, templateId];
            return;
        }
        loadingTemplateIds.value = [...loadingTemplateIds.value, templateId];
        attachedTemplateIds.value = [...attachedTemplateIds.value, templateId];
        try {
            await serviceChargeRepository.attachTemplates(serviceId.value, [templateId]);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            attachedTemplateIds.value = attachedTemplateIds.value.filter((id) => id !== templateId);
            throw err;
        }
        finally {
            if (version === scopeVersion) {
                loadingTemplateIds.value = loadingTemplateIds.value.filter((id) => id !== templateId);
            }
        }
    }
    async function detachTemplate(templateId) {
        const version = scopeVersion;
        if (!serviceId.value) {
            // Pending mode: just remove from local state
            attachedTemplateIds.value = attachedTemplateIds.value.filter((id) => id !== templateId);
            return;
        }
        const wasAttached = attachedTemplateIds.value.includes(templateId);
        loadingTemplateIds.value = [...loadingTemplateIds.value, templateId];
        attachedTemplateIds.value = attachedTemplateIds.value.filter((id) => id !== templateId);
        try {
            await serviceChargeRepository.detachTemplate(serviceId.value, templateId);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            if (wasAttached) {
                attachedTemplateIds.value = [...attachedTemplateIds.value, templateId];
            }
            throw err;
        }
        finally {
            if (version === scopeVersion) {
                loadingTemplateIds.value = loadingTemplateIds.value.filter((id) => id !== templateId);
            }
        }
    }
    async function addCustomCharge(dto) {
        const version = scopeVersion;
        if (!serviceId.value) {
            // Pending mode: create synthetic charge with negative temp ID for display
            const tempId = -++nextTempId;
            const synthetic = {
                id: tempId,
                service_id: 0,
                organization_id: 0,
                created_at: '',
                updated_at: '',
                ...dto,
            };
            pendingCustomCharges.value = [...pendingCustomCharges.value, { ...dto, _tempId: tempId }];
            customCharges.value = [...customCharges.value, synthetic];
            return synthetic;
        }
        isSaving.value = true;
        error.value = null;
        try {
            const created = await serviceChargeRepository.createCustom(serviceId.value, dto);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            customCharges.value = [...customCharges.value, created];
            return created;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = 'Error al crear el cargo personalizado';
            throw new Error(error.value);
        }
        finally {
            if (version === scopeVersion) {
                isSaving.value = false;
            }
        }
    }
    async function editCustomCharge(chargeId, dto) {
        const version = scopeVersion;
        if (chargeId < 0) {
            // Pending mode: update synthetic charge in both lists
            pendingCustomCharges.value = pendingCustomCharges.value.map((p) => p._tempId === chargeId ? { ...p, ...dto } : p);
            const updatedList = customCharges.value.map((c) => c.id === chargeId ? { ...c, ...dto } : c);
            customCharges.value = updatedList;
            return updatedList.find((c) => c.id === chargeId);
        }
        if (!serviceId.value)
            throw new Error('No service selected');
        isSaving.value = true;
        error.value = null;
        try {
            const updated = await serviceChargeRepository.updateCustom(serviceId.value, chargeId, dto);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            customCharges.value = customCharges.value.map((c) => (c.id === updated.id ? updated : c));
            return updated;
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            error.value = 'Error al actualizar el cargo personalizado';
            throw new Error(error.value);
        }
        finally {
            if (version === scopeVersion) {
                isSaving.value = false;
            }
        }
    }
    async function removeCustomCharge(chargeId) {
        const version = scopeVersion;
        if (chargeId < 0) {
            // Pending mode: remove synthetic charge from both lists
            pendingCustomCharges.value = pendingCustomCharges.value.filter((p) => p._tempId !== chargeId);
            customCharges.value = customCharges.value.filter((c) => c.id !== chargeId);
            return;
        }
        if (!serviceId.value)
            return;
        isSaving.value = true;
        error.value = null;
        try {
            await serviceChargeRepository.deleteCustom(serviceId.value, chargeId);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            customCharges.value = customCharges.value.filter((c) => c.id !== chargeId);
        }
        catch (err) {
            if (version !== scopeVersion)
                throw err;
            const status = getResponseStatus(err);
            if (status === 404) {
                customCharges.value = customCharges.value.filter((c) => c.id !== chargeId);
            }
            else {
                error.value = 'Error al eliminar el cargo personalizado';
                throw err;
            }
        }
        finally {
            if (version === scopeVersion) {
                isSaving.value = false;
            }
        }
    }
    async function applyPendingCharges(newServiceId) {
        const version = scopeVersion;
        serviceId.value = newServiceId;
        if (attachedTemplateIds.value.length > 0) {
            await serviceChargeRepository.attachTemplates(newServiceId, attachedTemplateIds.value);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
        }
        const createdCharges = [];
        for (const pending of pendingCustomCharges.value) {
            const { _tempId: _, ...dto } = pending;
            const created = await serviceChargeRepository.createCustom(newServiceId, dto);
            if (version !== scopeVersion)
                throw new Error('tenant_scope_changed');
            createdCharges.push(created);
        }
        // Replace synthetic (negative-ID) charges with real API responses
        customCharges.value = [...customCharges.value.filter((c) => c.id > 0), ...createdCharges];
        pendingCustomCharges.value = [];
        nextTempId = 0;
    }
    function $reset() {
        scopeVersion++;
        serviceId.value = null;
        attachedTemplateIds.value = [];
        customCharges.value = [];
        pendingCustomCharges.value = [];
        loadingTemplateIds.value = [];
        nextTempId = 0;
        isLoading.value = false;
        isSaving.value = false;
        error.value = null;
    }
    // ── RETURN ─────────────────────────────────────────────────────────────────
    return {
        serviceId,
        attachedTemplateIds,
        customCharges,
        loadingTemplateIds,
        isLoading,
        isSaving,
        error,
        isTemplateAttached,
        isTemplateLoading,
        initForService,
        attachTemplate,
        detachTemplate,
        addCustomCharge,
        editCustomCharge,
        removeCustomCharge,
        applyPendingCharges,
        $reset,
    };
});
